(function () {
  'use strict';

  const BOGO_EXCLUDE_VARIANTS = [];
  
  let isUpdating = false;
  let updateQueued = false;
  let updateTimer = null;
  let bogoQtyChangeInProgress = false;

  // ─── Cart mutation interception ──────────────────────────────────────────────

  const CART_MUTATION_RE = /\/cart(\/add|\/change|\/update)?(\.js)?(\?|$)/;

  const origFetch = window.fetch;
  window.fetch = function (...args) {
    const result = origFetch.apply(this, args);
    const url = typeof args[0] === 'string' ? args[0] : (args[0] instanceof Request ? args[0].url : '');
    const method = (args[1]?.method ?? 'GET').toUpperCase();
    if (method !== 'GET' && CART_MUTATION_RE.test(url)) scheduleUpdate();
    return result;
  };

  const origXhrOpen = XMLHttpRequest.prototype.open;
  const origXhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this._bogoMethod = method;
    this._bogoUrl = url;
    return origXhrOpen.call(this, method, url, ...rest);
  };
  XMLHttpRequest.prototype.send = function (...args) {
    if (this._bogoMethod?.toUpperCase() !== 'GET' && CART_MUTATION_RE.test(this._bogoUrl ?? '')) {
      scheduleUpdate();
    }
    return origXhrSend.apply(this, args);
  };

  if (window.Shopify) {
    const prevOnCartUpdate = window.Shopify.onCartUpdate;
    window.Shopify.onCartUpdate = (cart) => {
      if (typeof prevOnCartUpdate === 'function') prevOnCartUpdate(cart);
      scheduleUpdate();
    };
  }

  document.addEventListener('cart:refresh', scheduleUpdate);
  document.addEventListener('cart:updated', scheduleUpdate);

  // ─── Scheduling ──────────────────────────────────────────────────────────────

  function scheduleUpdate() {
    clearTimeout(updateTimer);
    updateTimer = setTimeout(updateCartDrawerWithBOGO, 50);
  }

  // ─── Loading overlay ─────────────────────────────────────────────────────────

  function showOverlay(on) {
    const overlay = document.querySelector('[data-atc-screen-overlay]');
    if (overlay) {
      // product-form.js leaves an inline `display:none` on the overlay after an
      // add-to-cart, which outranks the `.is-visible` class rule — clear it so
      // toggling the class actually shows/hides the loader (same guard as
      // cart-drawer.js._showScreenOverlay and cart-mode-bau.js).
      overlay.style.removeProperty('display');
      overlay.classList.toggle('is-visible', on);
    }
    const itemsEl = document.querySelector('[data-cart-items]');
    if (itemsEl) itemsEl.style.pointerEvents = on ? 'none' : '';
  }

  // ─── Config ──────────────────────────────────────────────────────────────────

  function getBogoConfig() {
    const el = document.getElementById('bogo-config');
    if (!el) return {};
    try { return JSON.parse(el.textContent); } catch { return {}; }
  }

  // Single source of truth for the max-free-regular-items cap, read from the
  // cart_mode_bogo_messages metaobject (field: bogo_max_free_regular_count) via
  // #bogo-config. Both renderBOGO() and bogoCalcBundleTotal() call this so the
  // fallback can only ever be defined once — the two-copies-can-drift bug this
  // replaced happened because the old hardcoded number lived in two places.
  const DEFAULT_MAX_FREE_REGULAR = 3;
  function getMaxFreeRegular(config) {
    const n = Math.floor(Number(config?.maxFreeRegular));
    return Number.isFinite(n) && n > 0 ? n : DEFAULT_MAX_FREE_REGULAR;
  }

  // Optional per-product-type quantity cap on BOGO participation, read from the
  // cart_mode_bogo_messages metaobject (fields: bogo_capped_product_type — a
  // plain Single line text field, e.g. "Sunscreen" or "Sunscreen,Lip" — and
  // bogo_capped_product_type_max_qty, one shared number applied independently
  // to EACH listed type). Blank field (the default, until a merchant sets it)
  // means this feature is fully off — every unit behaves exactly as it did
  // before this was added. When set, only the first N units of EACH listed
  // type (by cart order, counted separately per type) stay eligible for BOGO
  // at all; unit N+1 of that same type onward is treated as fully excluded,
  // same as any product on the existing excludedKeys list — always paid,
  // never free, never counted toward the free-count denominator either.
  const DEFAULT_CAPPED_TYPE_MAX_QTY = 4;
  function getCappedProductTypes(config) {
    const raw = config?.cappedProductType;
    // bogo_capped_product_type is a plain Single line text metaobject field —
    // a merchant types one type ("Sunscreen") or several, comma-separated
    // ("Sunscreen,Lip"), with no special Shopify field configuration needed.
    // Array is accepted too in case this field is ever changed to a List type
    // later, so this code wouldn't need to change again.
    const list = Array.isArray(raw) ? raw : String(raw || '').split(',');
    return new Set(list.map((t) => String(t).trim().toLowerCase()).filter(Boolean));
  }
  function getCappedProductTypeMaxQty(config) {
    const n = Math.floor(Number(config?.cappedProductTypeMaxQty));
    return Number.isFinite(n) && n > 0 ? n : DEFAULT_CAPPED_TYPE_MAX_QTY;
  }

  // ─── Main update ─────────────────────────────────────────────────────────────

  function updateCartDrawerWithBOGO() {
    // A trigger arriving while a fetch is already in flight (e.g. adding a 2nd
    // product while the 1st add's recalculation hasn't finished yet) must not be
    // silently dropped — queue one trailing re-run so the cart is always
    // recalculated against the latest /cart.js state once the in-flight cycle ends.
    if (isUpdating) {
      updateQueued = true;
      return;
    }
    isUpdating = true;
    origFetch('/cart.js')
      .then((r) => r.json())
      .then((cart) => {
        const config = getBogoConfig();
        renderBOGO(cart, config);
        updateCartCounts(cart.item_count);
      })
      .catch(() => {})
      .finally(() => {
        isUpdating = false;
        if (updateQueued) {
          updateQueued = false;
          updateCartDrawerWithBOGO();
        }
      });
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  function formatRupees(paise) {
    const rupees = Math.round(paise) / 100;
    return '₹' + rupees.toLocaleString('en-IN');
  }

  function escHtml(str) {
    return String(str ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function thumbUrl(url) {
    if (!url) return '';
    return url.replace(/(\.[^./?]+)(\?|$)/, '_120x$1$2');
  }

  function updateCartCounts(itemCount) {
    document.querySelectorAll('[data-cart-item-count]').forEach((el) => {
      el.textContent = `(${itemCount})`;
    });
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = itemCount;
    });
  }

  // ─── SVG icons ───────────────────────────────────────────────────────────────

  const ICON_TRASH = '<svg width="10" height="11" viewBox="0 0 11 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d="M4.32785 0.0917969H6.26279C6.38701 0.0917969 6.49521 0.0917969 6.59711 0.10844C6.79559 0.141455 6.98387 0.222245 7.1469 0.344347C7.30992 0.466448 7.44316 0.626472 7.53595 0.811616C7.58404 0.90672 7.61782 1.01312 7.65732 1.13497L7.72086 1.33409C7.7647 1.49587 7.86006 1.63743 7.99138 1.73567C8.1227 1.83391 8.28222 1.88303 8.44389 1.875H10.1613C10.2752 1.875 10.3844 1.92197 10.4649 2.00557C10.5454 2.08917 10.5906 2.20256 10.5906 2.3208C10.5906 2.43903 10.5454 2.55242 10.4649 2.63603C10.3844 2.71963 10.2752 2.7666 10.1613 2.7666H0.42935C0.315479 2.7666 0.206272 2.71963 0.125754 2.63603C0.0452349 2.55242 0 2.43903 0 2.3208C0 2.20256 0.0452349 2.08917 0.125754 2.00557C0.206272 1.92197 0.315479 1.875 0.42935 1.875H2.19827C2.35118 1.87119 2.49889 1.81661 2.61974 1.71927C2.74059 1.62192 2.82822 1.48693 2.86978 1.33409L2.93389 1.13497C2.97282 1.01312 3.0066 0.90672 3.05411 0.811616C3.14696 0.6264 3.28029 0.46633 3.44342 0.344224C3.60655 0.222118 3.79495 0.141368 3.99353 0.10844C4.09543 0.0917969 4.20362 0.0917969 4.32728 0.0917969M3.58193 1.875C3.62212 1.79326 3.65563 1.70816 3.68211 1.62059L3.73935 1.44227C3.79145 1.28 3.80347 1.24731 3.81549 1.22354C3.8464 1.16172 3.89082 1.10829 3.9452 1.06751C3.99958 1.02674 4.0624 0.999765 4.12863 0.988747C4.20326 0.982203 4.2782 0.980416 4.35304 0.983397H6.23645C6.40132 0.983397 6.43567 0.984586 6.46086 0.989341C6.52704 1.0003 6.58983 1.02718 6.6442 1.06785C6.69858 1.10852 6.74303 1.16183 6.774 1.22354C6.78602 1.24731 6.79804 1.28 6.85014 1.44287L6.90738 1.62119L6.92971 1.68776C6.95204 1.75315 6.97837 1.81496 7.00757 1.875H3.58193Z" fill="black"/><path d="M1.81192 3.77702C1.80433 3.65902 1.7519 3.54899 1.66618 3.47113C1.58045 3.39326 1.46845 3.35395 1.3548 3.36183C1.24116 3.36971 1.13519 3.42415 1.0602 3.51316C0.985208 3.60217 0.947344 3.71846 0.954935 3.83646L1.22056 7.96873C1.26922 8.73075 1.30872 9.34655 1.40146 9.8304C1.49821 10.3327 1.66193 10.7523 2.00083 11.081C2.33916 11.4103 2.75363 11.5524 3.24309 11.6184C3.71365 11.6825 4.30787 11.6825 5.04407 11.6825H5.54726C6.28288 11.6825 6.87768 11.6825 7.34825 11.6184C7.83713 11.5524 8.2516 11.4103 8.5905 11.081C8.92883 10.7523 9.09255 10.3321 9.1893 9.8304C9.28204 9.34715 9.32097 8.73075 9.3702 7.96873L9.63582 3.83646C9.64341 3.71846 9.60555 3.60217 9.53056 3.51316C9.45557 3.42415 9.3496 3.36971 9.23595 3.36183C9.12231 3.35395 9.01031 3.39326 8.92458 3.47113C8.83886 3.54899 8.78643 3.65902 8.77884 3.77702L8.5155 7.87838C8.46398 8.67904 8.42734 9.23659 8.3472 9.65564C8.26877 10.0628 8.16 10.278 8.00372 10.4301C7.84686 10.5823 7.63276 10.681 7.23661 10.7345C6.82844 10.7898 6.29033 10.7909 5.51692 10.7909H5.07383C4.301 10.7909 3.76289 10.7898 3.35414 10.7345C2.958 10.681 2.74389 10.5823 2.58704 10.4301C2.43075 10.278 2.32199 10.0628 2.24356 9.65624C2.16341 9.23659 2.12677 8.67904 2.07525 7.87779L1.81192 3.77702Z" fill="black"/><path d="M3.8212 4.84939C3.93446 4.83761 4.04759 4.873 4.13572 4.94779C4.22385 5.02258 4.27978 5.13065 4.2912 5.24823L4.57743 8.22024C4.58582 8.33619 4.55023 8.45096 4.47827 8.54006C4.40631 8.62916 4.30367 8.68554 4.19224 8.69718C4.0808 8.70881 3.96941 8.67477 3.8818 8.60233C3.7942 8.52988 3.73733 8.42477 3.72331 8.3094L3.43708 5.33739C3.42573 5.2198 3.45981 5.10234 3.53185 5.01083C3.60388 4.91932 3.70795 4.86125 3.8212 4.84939ZM6.76941 4.84939C6.88255 4.86126 6.98653 4.91924 7.05855 5.01061C7.13056 5.10199 7.16472 5.2193 7.15353 5.3368L6.8673 8.3088C6.8531 8.42395 6.79621 8.52881 6.70871 8.60108C6.62121 8.67335 6.51001 8.70733 6.39876 8.69579C6.28751 8.68426 6.18497 8.62812 6.11297 8.53931C6.04096 8.4505 6.00516 8.33603 6.01318 8.22024L6.29941 5.24823C6.31084 5.13076 6.36668 5.02279 6.45469 4.94802C6.54269 4.87324 6.65624 4.83777 6.76941 4.84939Z" fill="black"/></svg>';
  const ICON_MINUS = '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" fill="none" viewBox="0 0 10 2" width="10"><path fill-rule="evenodd" clip-rule="evenodd" d="M.5 1C.5.7.7.5 1 .5h8a.5.5 0 110 1H1A.5.5 0 01.5 1z" fill="currentColor"/></svg>';
  const ICON_PLUS  = '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" fill="none" viewBox="0 0 10 10" width="10"><path fill-rule="evenodd" clip-rule="evenodd" d="M1 4.51a.5.5 0 000 1h3.5l.01 3.5a.5.5 0 001-.01V5.5l3.5-.01a.5.5 0 00-.01-1H5.5L5.49.99a.5.5 0 00-1 .01v3.5l-3.5.01H1z" fill="currentColor"/></svg>';

  // ─── Row builder ─────────────────────────────────────────────────────────────

  // ─── Group units by (line-item key + free/paid status) ──────────────────────
  // Units with the same product key and the same free/paid status are merged
  // into a single display row. groupCount = units in this group (shown in the
  // qty stepper). quantity = total line-item qty (drives the +/- API call).

  function groupUnits(unitArray) {
    const map = new Map();
    const order = [];
    unitArray.forEach((unit) => {
      const gk = `${unit.key}-${unit.isFreeItem}`;
      if (!map.has(gk)) {
        const g = { ...unit, groupCount: 0 };
        map.set(gk, g);
        order.push(g);
      }
      map.get(gk).groupCount += 1;
    });
    return order;
  }

  function buildUnitRow(unit, rowNum, showVendor, eyePatchEnabled, excludeProductOfferText) {
    const { key, variantId, productId, title, variantTitle, vendor, url, image, imageAlt, price, compareAtPrice, quantity, isFreeItem, isExcluded, isCappedBogo, isTypeAtCap, groupCount } = unit;

    // groupCount = units in this display group (shown in stepper)
    // quantity   = total line-item qty (drives +/− API call)
    const displayQty = groupCount ?? quantity;

    const eyePatchClass = eyePatchEnabled ? ` eye-patch-${productId}` : '';
    const minusIcon = quantity <= 1 ? ICON_TRASH : ICON_MINUS;
    const minusLabel = quantity <= 1 ? 'Remove item' : 'Decrease quantity';
    // capped-product-bogo caps the line at 1 unit — the increase control is omitted
    // entirely rather than disabled, so there's no plus button to hide/gray out.
    // isTypeAtCap (per-product-type quantity cap) instead DISABLES the button —
    // the product itself can still be bought elsewhere/later, just not right now,
    // so a grayed-out control reads better here than removing it outright.
    const plusHtml = isCappedBogo
      ? ''
      : `<button type="button" name="plus" class="cart-line-item__qty-btn cart-line-item__qty-plus${eyePatchClass}"${isTypeAtCap ? ' disabled aria-disabled="true" data-type-capped="true"' : ''} aria-label="Increase quantity">${ICON_PLUS}</button>`;

    const freeBadge = isFreeItem
      ? '<img src="https://cdn.shopify.com/s/files/1/0361/8553/8692/files/icon-free.png?v=1732515817" class="cart-icon-free" loading="lazy" alt="Free item">'
      : '';

    const imgHtml = image
      ? `<a href="${escHtml(url)}" class="flex-shrink-0" tabindex="-1" aria-hidden="true">
          <div class="relative">${freeBadge}<img src="${thumbUrl(image)}" alt="${escHtml(imageAlt)}" width="68" height="68" loading="lazy" class="cart-line-item__image"></div>
         </a>`
      : '';

    const variantHtml = variantTitle
      ? `<p class="cart-line-item__variant">${escHtml(variantTitle)}</p>`
      : '';

    const vendorHtml = showVendor && vendor
      ? `<p class="text-xs text-muted">${escHtml(vendor)}</p>`
      : '';

    const excludedLabel = isExcluded
      ? `<p class="cart-line-item__excluded-label">${escHtml(excludeProductOfferText)}</p>`
      : '';

    // Price shown is the LINE total for this row (unit price × units in this display
    // group), matching the convention used elsewhere in the cart (e.g. cart-line-item.liquid's
    // original_line_price/final_line_price) — not a flat per-unit price.
    // A non-free row still shows its own MRP strikethrough when the variant carries a
    // real compare_at_price (unrelated to the BOGO free-slot mechanic) — cart.js has no
    // compare_at_price field, so compareAtPrice is handed in server-side (see cart-mode-bogo.liquid).
    const hasCompareAtPrice = compareAtPrice && compareAtPrice > price;
    const priceHtml = isFreeItem
      ? `<div class="cart-line-item__price-row">
          <s class="cart-line-item__compare">${formatRupees(price * displayQty)}</s>
          <span class="cart-line-item__price">${formatRupees(100 * displayQty)}</span>
         </div>`
      : hasCompareAtPrice
      ? `<div class="cart-line-item__price-row">
          <s class="cart-line-item__compare">${formatRupees(compareAtPrice * displayQty)}</s>
          <span class="cart-line-item__price">${formatRupees(price * displayQty)}</span>
         </div>`
      : `<div class="cart-line-item__price-row">
          <span class="cart-line-item__price">${formatRupees(price * displayQty)}</span>
         </div>`;

    return `<div class="cart-drawer__item-row">
      <div class="cart-line-item relative flex gap-3" id="CartItem-${rowNum}" data-line-index="${rowNum}">
        <div class="cart-line-item__loading" aria-hidden="true"><span style="display:flex;justify-content:center;align-items:center;"><img width="20" height="20" src="https://cdn.shopify.com/s/files/1/0361/8553/8692/files/Dot-_-Key-Loading-Animation-White.gif?v=1724248882&amp;width=20" alt="" aria-hidden="true"></span></div>
        ${imgHtml}
        <div class="cart-line-item__content">
          ${vendorHtml}
          <a href="${escHtml(url)}" class="cart-line-item__title">${escHtml(title)}</a>
          <div class="cart-line-item__row-meta">
            <div>${variantHtml}</div>
            <div class="cart-line-item__qty-row" data-key="${escHtml(key)}" data-variant-id="${variantId}" data-line-qty="${quantity}">
              <button type="button" name="minus" class="cart-line-item__qty-btn cart-line-item__qty-minus${quantity <= 1 ? ' cart-line-item__qty-remove' : ''}" aria-label="${minusLabel}">${minusIcon}</button>
              <input type="number" value="${displayQty}" disabled class="cart-line-item__qty-input" aria-live="polite">
              ${plusHtml}
            </div>
          </div>
          ${priceHtml}
          ${excludedLabel}
        </div>
      </div>
    </div>`;
  }

  // ─── BOGO algorithm + render ──────────────────────────────────────────────────

  function renderBOGO(cart, config) {
    const target = document.querySelector('[data-bogo-item-rows]') ?? document.querySelector('[data-cart-items]');
    if (!target) {
      if (bogoQtyChangeInProgress) { showOverlay(false); bogoQtyChangeInProgress = false; }
      return;
    }

    const excludedKeys   = new Set(config.excludedKeys ?? []);
    const cappedBogoKeys = new Set(config.cappedBogoKeys ?? []);
    const cappedOfferKeys = new Set(config.cappedOfferKeys ?? []);
    const showVendor = !!config.showVendor;
    const eyePatchEnabled = !!config.eyePatchEnabled;
    const excludeProductOfferText = config.excludeProductOfferText || 'Offer not valid for this product';
    const compareAtPrices = config.compareAtPrices ?? {};
    const maxFreeRegular = getMaxFreeRegular(config);
    const cappedProductTypes = getCappedProductTypes(config);
    const cappedProductTypeMaxQty = getCappedProductTypeMaxQty(config);
    const cappedProductTypeSeen = {}; // normalized type -> units seen so far, counted independently per type

    // Step 1: flatten line items to individual unit objects
    const units = [];
    cart.items.forEach((item, idx) => {
      const isLineExcluded = excludedKeys.has(item.key) || BOGO_EXCLUDE_VARIANTS.includes(Number(item.variant_id));
      const isCombo = item.product_type.toLowerCase().includes('combo');
      // Per-product-type BOGO participation cap (see getCappedProductTypes above).
      // Counted in cart order across every line of a given type, independently
      // per type — cappedProductTypes is empty (feature off) unless a merchant
      // has actually set the metaobject field, so isCappedType is always false
      // and this never touches isExcluded below until that's configured.
      const itemTypeKey = item.product_type.trim().toLowerCase();
      const isCappedType = cappedProductTypes.has(itemTypeKey);
      // capped-product-bogo: quantity is capped at 1 by omitting the plus control below and
      // by product-form.js blocking a repeat add-to-cart, but any unit beyond the first
      // (e.g. quantity synced in from elsewhere) is still pinned paid here as a second line
      // of defense. capped-product-offer: any quantity may be in the cart by design, and
      // only its first unit competes for the free slot. Either way, only unit 0 per line
      // can ever win a free slot — the rest (isCapExtra) still count toward the free-count
      // denominator (see Step 2/3/4 below) but are excluded from the free-slot candidates.
      const isCappedBogo  = cappedBogoKeys.has(item.key);
      const isCappedOffer = !isCappedBogo && cappedOfferKeys.has(item.key);
      for (let i = 0; i < item.quantity; i++) {
        let isExcluded = isLineExcluded;
        if (!isExcluded && isCappedType) {
          cappedProductTypeSeen[itemTypeKey] = (cappedProductTypeSeen[itemTypeKey] || 0) + 1;
          if (cappedProductTypeSeen[itemTypeKey] > cappedProductTypeMaxQty) isExcluded = true;
        }
        units.push({
          key: item.key,
          variantId: item.variant_id,
          productId: item.product_id,
          title: item.product_title || item.title,
          variantTitle: item.variant_title || '',
          vendor: item.vendor ?? '',
          url: item.url || '/',
          image: item.featured_image?.url ?? null,
          imageAlt: item.featured_image?.alt ?? '',
          price: item.original_price,
          compareAtPrice: compareAtPrices[item.variant_id] ?? null,
          quantity: item.quantity,
          isExcluded,
          isCombo,
          isCappedBogo,
          isCapExtra: (isCappedBogo || isCappedOffer) && i > 0,
          isFreeItem: false,
          isTypeAtCap: false, // backfilled below once every line has been counted
          lineIdx: idx,
          productTypeKey: itemTypeKey,
        });
      }
    });

    // Every line for a capped type has now been counted (cappedProductTypeSeen
    // holds the cart-wide final total per type), so the plus-button lockout can
    // be decided — including for units that stayed eligible themselves (e.g. the
    // 4th sunscreen unit): once the type's total is at/over the cap, adding a
    // 5th of that SAME type would only create another excluded unit, so the
    // stepper is locked on every row of that type, not just the excluded ones.
    units.forEach((u) => {
      if (!cappedProductTypes.has(u.productTypeKey)) return;
      u.isTypeAtCap = (cappedProductTypeSeen[u.productTypeKey] || 0) >= cappedProductTypeMaxQty;
    });

    // Step 2: categorise. capExtra units (quantity beyond the first unit of a capped
    // line) stay IN the regular/combo pool — they still count toward the free-count
    // denominator, same as any other eligible item — but are excluded from the
    // candidate list in steps 3/4 below, so they can never personally be selected
    // as the free unit. This keeps a capped line's cap on how many of ITS OWN units
    // can win a free slot, without shrinking the free-slot count owed to the rest
    // of the cart.
    const excludedUnits = units.filter((u) => u.isExcluded);
    const comboUnits    = units.filter((u) => !u.isExcluded && u.isCombo);
    const regularUnits  = units.filter((u) => !u.isExcluded && !u.isCombo);

    // Step 3: apply BOGO to regular (max maxFreeRegular free, lowest-price first among candidates)
    const regularCandidates = regularUnits.filter((u) => !u.isCapExtra).sort((a, b) => a.price - b.price);
    const regularFreeCount = Math.min(maxFreeRegular, Math.floor(regularUnits.length / 2));
    regularCandidates.slice(0, regularFreeCount).forEach((u) => { u.isFreeItem = true; });

    // Step 4: apply BOGO to combo (max 1 free)
    const comboCandidates = comboUnits.filter((u) => !u.isCapExtra).sort((a, b) => a.price - b.price);
    const comboFreeCount = Math.min(1, Math.floor(comboUnits.length / 2));
    comboCandidates.slice(0, comboFreeCount).forEach((u) => { u.isFreeItem = true; });

    // Step 5: build render order and group into display rows
    // Category order: excluded → paid regular → paid combo → free regular → free combo
    // Within each category, units with the same line-item key and free/paid status
    // are merged into one row (groupCount = number of units, shown in the qty stepper).
    // capExtra units are never marked free, so they fall into the paid group of their own key.
    const paidRegular = regularUnits.filter((u) => !u.isFreeItem);
    const paidCombo   = comboUnits.filter((u) => !u.isFreeItem);
    const freeRegular = regularUnits.filter((u) => u.isFreeItem);
    const freeCombo   = comboUnits.filter((u) => u.isFreeItem);
    const renderOrder = [
      ...groupUnits(excludedUnits),
      ...groupUnits(paidRegular),
      ...groupUnits(paidCombo),
      ...groupUnits(freeRegular),
      ...groupUnits(freeCombo),
    ];

    const rowsHtml = renderOrder.map((unit, i) => buildUnitRow(unit, i + 1, showVendor, eyePatchEnabled, excludeProductOfferText)).join('');

    // Pre-compute savings/total before the rAF so all DOM writes happen in one frame
    const freebiesMrpEl = document.getElementById('freebies_count_pmrptam');
    const freebiesCtEl  = document.getElementById('freebies_countsd_s');
    const freebiesMrpRupees = parseFloat(freebiesMrpEl?.value ?? '0') || 0;
    const freebiesCount     = parseInt(freebiesCtEl?.value ?? '0', 10) || 0;

    const originalTotalRupees = units.reduce((s, u) => s + u.price, 0) / 100;
    const paidTotal           = [...paidRegular, ...paidCombo, ...excludedUnits].reduce((s, u) => s + u.price / 100, 0);
    const freeTotal           = (freeRegular.length + freeCombo.length) * 1;
    const displayedTotal      = paidTotal + freeTotal;
    const discountTotal       = Math.round((originalTotalRupees + freebiesMrpRupees) - (displayedTotal + freebiesCount));
    // BOGO subtotal = paid items at full price + free BOGO items at ₹1 + freebie-section items at ₹1
    const cartTotalPaise      = Math.round((paidTotal + freeTotal + freebiesCount) * 100);

    requestAnimationFrame(() => {
      target.innerHTML = rowsHtml;

      const savingsEl = document.querySelector('[data-bogo-savings]');
      if (savingsEl) {
        if (discountTotal > 0) {
          savingsEl.textContent = `You Save: ₹${discountTotal}`;
          savingsEl.classList.remove('hidden');
        } else {
          savingsEl.textContent = '';
          savingsEl.classList.add('hidden');
        }
      }

      const bogoTotalAmountEl = document.querySelector('[data-bogo-total-amount]');
      if (bogoTotalAmountEl) {
        bogoTotalAmountEl.textContent = formatRupees(cartTotalPaise);
      }

      const cartPageOrderTotalEl = document.querySelector('[data-cart-page-order-total]');
      const cartPageGrandTotalEl = document.querySelector('[data-cart-page-grand-total]');
      const cartPageSavingsRowEl = document.querySelector('[data-cart-page-savings-row]');
      const cartPageSavingsEl    = document.querySelector('[data-cart-page-savings]');

      if (cartPageOrderTotalEl) {
        cartPageOrderTotalEl.textContent = formatRupees(Math.round(originalTotalRupees * 100));
      }
      if (cartPageGrandTotalEl) {
        cartPageGrandTotalEl.textContent = formatRupees(cartTotalPaise);
      }
      if (cartPageSavingsRowEl && cartPageSavingsEl && discountTotal > 0) {
        cartPageSavingsEl.textContent = `-₹${discountTotal.toLocaleString('en-IN')}`;
        cartPageSavingsRowEl.classList.add('is-visible');
      }

      // Hide loading overlay if this render was triggered by a BOGO qty change
      if (bogoQtyChangeInProgress) {
        showOverlay(false);
        bogoQtyChangeInProgress = false;
      }
    });
  }

  // ─── Delegated quantity handler ───────────────────────────────────────────────

  document.body.addEventListener('click', (e) => {
    const btn = e.target.closest('button[name="plus"], button[name="minus"]');
    if (!btn) return;
    const controls = btn.closest('.cart-line-item__qty-row');
    if (!controls?.dataset.key) return;

    const cartDrawerEl = document.querySelector('cart-drawer');
    const cartPageEl   = document.querySelector('[data-cart-page]');
    const key       = controls.dataset.key;
    const lineQty   = parseInt(controls.dataset.lineQty ?? '1', 10);
    const isPlusBtn = btn.name === 'plus';
    const newQty    = isPlusBtn ? lineQty + 1 : Math.max(0, lineQty - 1);
    const sections  = [];
    if (cartDrawerEl?.dataset.sectionId) sections.push(cartDrawerEl.dataset.sectionId);
    if (cartPageEl?.dataset.sectionId) sections.push(cartPageEl.dataset.sectionId);
    const sectionId = cartDrawerEl?.dataset.sectionId ?? 'cart-drawer';

    // Show per-item loader immediately on click
    const itemEl = btn.closest('.cart-line-item');
    const loadingEl = itemEl?.querySelector('.cart-line-item__loading');
    if (loadingEl) loadingEl.classList.add('flex');

    bogoQtyChangeInProgress = true;
    showOverlay(true);

    // Pass sections:[sectionId] so Shopify returns both the updated cart AND fresh
    // section HTML in one request — same pattern as CartDrawer.updateQuantity uses.
    origFetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: newQty, sections }),
    })
      .then((r) => r.json())
      .then((data) => {
        const sectionHtml = data?.sections?.[sectionId] ?? data?.sections?.['cart-drawer'];
        if (cartDrawerEl && sectionHtml) {
          cartDrawerEl.renderContents(data);
        }

        const cartPageSectionId = cartPageEl?.dataset.sectionId;
        if (cartPageEl && cartPageSectionId && data?.sections?.[cartPageSectionId]) {
          const parsed = new DOMParser().parseFromString(data.sections[cartPageSectionId], 'text/html');
          const newContent = parsed.querySelector('[data-cart-page]');
          if (newContent) {
            cartPageEl.innerHTML = newContent.innerHTML;
          }
        }

        // Removing the last line empties the cart, so the re-rendered section is
        // the empty state — it has no [data-bogo-item-rows], so the MutationObserver
        // that normally chains into scheduleUpdate() → renderBOGO() (which is what
        // clears the loading overlay on a qty change) never fires. Clear it here.
        if (!sectionHtml || data?.item_count === 0) {
          showOverlay(false);
          bogoQtyChangeInProgress = false;
        }

        if (!sectionHtml) {
          scheduleUpdate();
        }
      })
      .catch(() => {
        showOverlay(false);
        bogoQtyChangeInProgress = false;
        scheduleUpdate();
      });
  });

  // ─── Initialise: initial render + post-renderContents watcher ────────────────

  function bogoInit() {
    // Render BOGO prices immediately on script load (handles page load / direct cart open)
    if (document.querySelector('[data-bogo-item-rows]')) {
      scheduleUpdate();
    }

    // Re-fire BOGO every time CartDrawer.renderContents() replaces the section HTML.
    // renderContents does `this.innerHTML = newHtml` on the <cart-drawer> element,
    // which triggers a childList mutation on that element's direct children.
    // We reset the debounce timer here so BOGO always fires AFTER the Liquid re-render.
    const cartDrawerEl = document.querySelector('cart-drawer');
    if (cartDrawerEl) {
      new MutationObserver((mutations) => {
        const hasAddedNodes = mutations.some((m) => m.addedNodes.length > 0);
        if (hasAddedNodes && document.querySelector('[data-bogo-item-rows]')) {
          scheduleUpdate();
        }
      }).observe(cartDrawerEl, { childList: true });
    }

    const cartPageEl = document.querySelector('[data-cart-page]');
    if (cartPageEl) {
      new MutationObserver((mutations) => {
        const hasAddedNodes = mutations.some((m) => m.addedNodes.length > 0);
        if (hasAddedNodes && document.querySelector('[data-bogo-item-rows]')) {
          scheduleUpdate();
        }
      }).observe(cartPageEl, { childList: true });
    }
  }

  // ── Bundle total for Customers Also Bought (and any future bundle section) ─
  // Pure BOGO calculation — no DOM reads/writes, no /cart.js fetch.
  // Mirrors the algorithm in renderBOGO(): normal items capped via
  // getMaxFreeRegular() (metaobject-driven, see #bogo-config), combo max 1 free.
  //
  // items: [{ price (paise), productType }]
  // Returns discounted total in paise (free items cost ₹1 = 100 paise).
  function bogoCalcBundleTotal(items) {
    if (!items || !items.length) return 0;

    var maxFreeRegular = getMaxFreeRegular(getBogoConfig());
    var normal = [], combo = [];
    items.forEach(function (item) {
      var type = String(item.productType || '').trim().toLowerCase();
      var price = Number(item.price || 0);
      if (type.indexOf('combo') !== -1) { combo.push(price); }
      else { normal.push(price); }
    });

    function applyBogo(prices, maxFree) {
      var sorted = prices.slice().sort(function (a, b) { return a - b; });
      var freeCount = Math.min(maxFree, Math.floor(sorted.length / 2));
      return sorted.reduce(function (sum, price, idx) {
        return sum + (idx < freeCount ? 100 : price); // free = ₹1 = 100 paise
      }, 0);
    }

    return applyBogo(normal, maxFreeRegular) + applyBogo(combo, 1);
  }

  window.DnkPricing = { calcBundleTotal: bogoCalcBundleTotal };

  bogoInit();
})();
